import { useEffect, useState, type FormEvent, type ReactNode } from 'react'
import { Navigate, NavLink, useParams } from 'react-router-dom'

import { requestPasswordReset } from '../api/auth'
import { updateOrganization } from '../api/organizations'
import { listCities, listProductTypesL0 } from '../api/reference'
import { fetchMe, updateProfile } from '../api/me'
import type { MeResponse } from '../api/types'
import { useAuth } from '../auth/AuthProvider'
import { ApiError } from '../api/client'
import { MarketPageHeader } from '../components/MarketPageHeader'
import { InventoryConnectionSettings } from '../components/settings/InventoryConnectionSettings'
import { SelectCombobox } from '../components/SelectCombobox'
import { ogrnValidationMessage } from '../utils/ogrn'
import { PaymentsScreen } from './PaymentsScreen'

type SettingsTabKey = 'organization' | 'integrations' | 'billing'

const SettingsColumn = ({
  title,
  description,
  className,
  children,
}: {
  title: string
  description?: string
  className?: string
  children: ReactNode
}) => (
  <article className={['market-settings-column', className].filter(Boolean).join(' ')}>
    <header className="market-settings-column__head">
      <h2 className="market-settings-column__title">{title}</h2>
      {description ? <p className="market-settings-column__desc">{description}</p> : null}
    </header>
    <div className="market-settings-column__body">{children}</div>
  </article>
)

export const SettingsScreen = () => {
  const { session, organizationName, organizations, switchOrganization, updateProfileInSession } =
    useAuth()
  const [profile, setProfile] = useState<MeResponse | null>(null)
  const [sendingReset, setSendingReset] = useState(false)
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [legalName, setLegalName] = useState('')
  const [tradeName, setTradeName] = useState('')
  const [ogrn, setOgrn] = useState('')
  const [city, setCity] = useState('moscow')
  const [productTypeIds, setProductTypeIds] = useState<number[]>([])
  const [contactName, setContactName] = useState('')
  const [contactPhone, setContactPhone] = useState('')
  const [cities, setCities] = useState<{ code: string; label: string }[]>([])
  const [supplierTypes, setSupplierTypes] = useState<{ id: number; name: string }[]>([])
  const [loading, setLoading] = useState(true)
  const [savingProfile, setSavingProfile] = useState(false)
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const currentOrg = profile?.organizations.find((org) => org.id === session?.organizationId)
  const currentOrgLabel =
    organizationName ?? currentOrg?.trade_name ?? currentOrg?.legal_name ?? '—'
  const isBuyerOrg = currentOrg?.org_type === 'buyer'
  // Блок контактного лица показывается, только если сервер собирает ФИО и телефоны.
  // Иначе поля висели бы и молча не сохранялись: сервер при выключенном сборе их не пишет.
  const contactPersonEnabled = currentOrg?.contact_person_enabled === true
  const contactPhoneDigits = contactPhone.replace(/\D/g, '').length
  const contactPhoneError =
    contactPersonEnabled && contactPhone.trim() !== '' && (contactPhoneDigits < 10 || contactPhoneDigits > 15)
      ? 'Проверьте номер телефона: нужно 10–15 цифр.'
      : null

  useEffect(() => {
    void Promise.all([
      listCities().catch(() => []),
      listProductTypesL0().catch(() => []),
    ]).then(([cityOptions, typeOptions]) => {
      setCities(cityOptions)
      setSupplierTypes(typeOptions)
    })
  }, [])

  useEffect(() => {
    void (async () => {
      try {
        const me = await fetchMe()
        setProfile(me)
        setEmail(me.email ?? session?.email ?? '')
        const org = me.organizations.find((item) => item.id === session?.organizationId)
        if (org) {
          setLegalName(org.legal_name)
          setTradeName(org.trade_name ?? '')
          setOgrn(org.ogrn ?? '')
          setCity(org.city ?? 'moscow')
          setProductTypeIds(org.product_type_ids ?? [])
          setContactName(org.contact_name ?? '')
          setContactPhone(org.contact_phone ?? '')
        }
      } catch (loadError) {
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить профиль')
      } finally {
        setLoading(false)
      }
    })()
  }, [session?.organizationId, session?.email])

  const handlePasswordReset = async () => {
    setSendingReset(true)
    setError(null)
    try {
      await requestPasswordReset(email.trim())
      setPasswordMessage(`Ссылка для нового пароля отправлена на ${email.trim()}.`)
    } catch (resetError) {
      setError(resetError instanceof Error ? resetError.message : 'Не удалось отправить ссылку')
    } finally {
      setSendingReset(false)
    }
  }

  const handleProfileSubmit = async (event: FormEvent) => {
    event.preventDefault()
    setSavingProfile(true)
    setError(null)
    setMessage(null)
    try {
      const updated = await updateProfile({ email })
      setProfile(updated)
      updateProfileInSession({ displayName: updated.displayName, email: updated.email })
      setMessage('Профиль сохранён')
    } catch (saveError) {
      if (saveError instanceof ApiError) {
        setError(saveError.message || 'Не удалось сохранить профиль')
      } else {
        setError(saveError instanceof Error ? saveError.message : 'Не удалось сохранить профиль')
      }
    } finally {
      setSavingProfile(false)
    }
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (!session || session.organizationId == null || !currentOrg?.can_edit) {
      return
    }
    const ogrnError = ogrnValidationMessage(ogrn)
    if (ogrnError) {
      setError(ogrnError)
      return
    }
    if (contactPhoneError) {
      setError(contactPhoneError)
      return
    }
    setSaving(true)
    setError(null)
    setMessage(null)
    try {
      await updateOrganization(session.organizationId, {
        legalName: legalName.trim(),
        tradeName: tradeName.trim(),
        ogrn: ogrn.trim(),
        city,
        // Тип заведения не редактируем в настройках — сохраняем текущее значение без изменений.
        horecaType: currentOrg.org_type === 'buyer' ? currentOrg.horeca_type ?? null : null,
        productTypeIds: currentOrg.org_type === 'supplier' ? productTypeIds : undefined,
        // Пустая строка очищает контакт. При выключенном сборе поля не шлём вовсе.
        ...(contactPersonEnabled
          ? { contactName: contactName.trim(), contactPhone: contactPhone.trim() }
          : {}),
      })
      setMessage('Настройки организации сохранены')
    } catch (saveError) {
      if (saveError instanceof ApiError) {
        setError(saveError.message || 'Не удалось сохранить')
      } else {
        setError(saveError instanceof Error ? saveError.message : 'Не удалось сохранить')
      }
    } finally {
      setSaving(false)
    }
  }

  const toggleProductType = (typeId: number) => {
    setProductTypeIds((current) =>
      current.includes(typeId) ? current.filter((id) => id !== typeId) : [...current, typeId],
    )
  }

  const { tab } = useParams<{ tab?: string }>()
  const tabs: { key: SettingsTabKey; label: string }[] = [
    { key: 'organization', label: 'Организация' },
    ...(isBuyerOrg ? [{ key: 'integrations' as const, label: 'Интеграции' }] : []),
    ...(isBuyerOrg ? [{ key: 'billing' as const, label: 'Оплата и тариф' }] : []),
  ]
  const activeTab = tabs.find((item) => item.key === tab)?.key ?? null

  if (!loading && activeTab == null) {
    return <Navigate to="/settings/organization" replace />
  }

  return (
    <section className="market-page market-page--wide market-page--settings">
      <MarketPageHeader
        title="Настройки"
        description="Организация, интеграции и тариф."
      />

      {loading ? (
        <p className="echat-muted">Загрузка…</p>
      ) : (
        <>
          <div className="market-settings-status-bar" aria-live="polite">
            {message ? <p className="market-settings-status market-success">{message}</p> : null}
            {error ? (
              <p className="echat-error" role="alert">
                {error}
              </p>
            ) : null}
          </div>

          <div className="market-settings-layout">
            <nav className="market-settings-tabs" aria-label="Разделы настроек">
              <ul className="market-settings-tabs__list">
                {tabs.map((item) => (
                  <li key={item.key}>
                    <NavLink
                      to={`/settings/${item.key}`}
                      className={({ isActive }) =>
                        isActive ? 'market-settings-tab market-settings-tab--active' : 'market-settings-tab'
                      }
                    >
                      {item.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            <div className="market-settings-panel">
              {activeTab === 'organization' ? (
                <SettingsColumn title="Организация" description="Вход, реквизиты и контактное лицо">
                  <div className="market-settings-section">
                    <h3 className="market-settings-section__title">Вход</h3>
                    <div className="market-settings-fields-row">
                      <form className="market-form" onSubmit={(event) => void handleProfileSubmit(event)}>
                        <label className="echat-field">
                          <span className="echat-field__label">Корпоративная почта</span>
                          <input
                            className="echat-field__input"
                            type="email"
                            required
                            autoComplete="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                          />
                        </label>
                        <div className="market-actions-row">
                          <button
                            type="submit"
                            className="echat-btn echat-btn--primary echat-btn--sm"
                            disabled={savingProfile}
                          >
                            {savingProfile ? 'Сохранение…' : 'Сохранить профиль'}
                          </button>
                        </div>
                      </form>
                      <div className="market-settings-password">
                        <p className="echat-field__label">Пароль</p>
                        <p className="market-settings-password__hint">
                          {passwordMessage ?? 'Пришлём ссылку для нового пароля на вашу почту.'}
                        </p>
                        <div className="market-actions-row">
                          <button
                            type="button"
                            className="echat-btn echat-btn--ghost echat-btn--sm"
                            disabled={sendingReset || passwordMessage !== null}
                            onClick={() => void handlePasswordReset()}
                          >
                            {sendingReset ? 'Отправка…' : 'Сменить пароль'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="market-settings-section">
                    <h3 className="market-settings-section__title">Реквизиты</h3>
                    {currentOrg?.can_edit === false ? (
                <div>
                  <p className="echat-panel__text">{currentOrgLabel}</p>
                  <p className="echat-muted">У вас нет прав на редактирование настроек организации.</p>
                </div>
              ) : (
                <form className="market-form" onSubmit={(event) => void handleSubmit(event)}>
                  {organizations.length > 1 ? (
                    <SelectCombobox
                      label="Текущая организация"
                      value={String(session?.organizationId ?? '')}
                      options={organizations.map((org) => ({
                        value: String(org.id),
                        label: org.trade_name ?? org.legal_name,
                      }))}
                      onChange={(next) => void switchOrganization(Number(next))}
                    />
                  ) : null}

                  <div className="market-settings-fields-row">
                    <label className="echat-field">
                      <span className="echat-field__label">Юридическое название</span>
                      <input
                        className="echat-field__input"
                        value={legalName}
                        required
                        onChange={(event) => setLegalName(event.target.value)}
                      />
                    </label>

                    <label className="echat-field">
                      <span className="echat-field__label">Торговое название</span>
                      <input
                        className="echat-field__input"
                        value={tradeName}
                        required
                        onChange={(event) => setTradeName(event.target.value)}
                      />
                    </label>
                  </div>

                  {/*
                    pattern="[0-9]{10}" остался здесь от ИНН: браузер не давал отправить форму
                    с правильным ОГРН из 13 цифр и ОГРНИП из 15. Проверку делает
                    ogrnValidationMessage при отправке — она знает и длины, и контрольную цифру.
                  */}
                  <div className="market-settings-fields-row">
                    <label className="echat-field">
                      <span className="echat-field__label">ОГРН или ОГРНИП</span>
                      <input
                        className="echat-field__input"
                        required
                        inputMode="numeric"
                        autoComplete="off"
                        placeholder="13 или 15 цифр"
                        value={ogrn}
                        onChange={(event) => setOgrn(event.target.value)}
                      />
                    </label>

                    <SelectCombobox
                      label="Город"
                      value={city}
                      options={cities.map((option) => ({
                        value: option.code,
                        label: option.label,
                      }))}
                      onChange={setCity}
                      required
                    />
                  </div>

                  {currentOrg?.org_type === 'supplier' && supplierTypes.length > 0 ? (
                    <fieldset className="market-fieldset">
                      <legend className="echat-field__label">Категории товаров</legend>
                      <div className="market-checkbox-grid">
                        {supplierTypes.map((type) => (
                          <label key={type.id} className="market-filters__checkbox">
                            <input
                              type="checkbox"
                              checked={productTypeIds.includes(type.id)}
                              onChange={() => toggleProductType(type.id)}
                            />
                            <span>{type.name}</span>
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  ) : null}

                  {contactPersonEnabled ? (
                    <fieldset className="market-settings-subblock">
                      <legend className="market-settings-subblock__head">
                        <span className="market-settings-subblock__title">Контактное лицо</span>
                        <span className="market-settings-subblock__optional">необязательно</span>
                      </legend>
                      <div className="market-settings-fields-row market-settings-fields-row--wide-first">
                        <label className="echat-field">
                          <span className="echat-field__label">ФИО</span>
                          <input
                            className="echat-field__input"
                            autoComplete="name"
                            maxLength={200}
                            placeholder="Иван Петров"
                            value={contactName}
                            onChange={(event) => setContactName(event.target.value)}
                          />
                        </label>
                        <label className={`echat-field${contactPhoneError ? ' echat-field--invalid' : ''}`}>
                          <span className="echat-field__label">Телефон</span>
                          <input
                            className="echat-field__input"
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel"
                            maxLength={30}
                            placeholder="+7 900 000-00-00"
                            aria-invalid={contactPhoneError ? true : undefined}
                            value={contactPhone}
                            onChange={(event) => setContactPhone(event.target.value)}
                          />
                          {contactPhoneError ? (
                            <span className="echat-field__error" role="alert">
                              {contactPhoneError}
                            </span>
                          ) : null}
                        </label>
                      </div>
                    </fieldset>
                  ) : null}

                  <div className="market-actions-row">
                    <button type="submit" className="echat-btn echat-btn--primary echat-btn--sm" disabled={saving}>
                      {saving ? 'Сохранение…' : 'Сохранить организацию'}
                    </button>
                  </div>
                </form>
              )}
                  </div>
                </SettingsColumn>
              ) : null}

              {activeTab === 'integrations' ? <InventoryConnectionSettings /> : null}

              {activeTab === 'billing' ? <PaymentsScreen embedded /> : null}
            </div>
          </div>
        </>
      )}
    </section>
  )
}
